import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Search, PackageSearch, LogOut, User, Bell, X, Users, Stethoscope, ClipboardList, ChevronDown, ShoppingBag, LayoutDashboard, Briefcase, PauseCircle } from 'lucide-react';
import axios from 'axios';
import { clearSalesFlowSession } from '../../utils/salesSession';
import { useTranslation } from 'react-i18next';

const SidebarContainer = styled.nav`
  width: 260px;
  height: 100vh;
  background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const UserCard = styled.div`
  padding: 2rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => props.hasImage ? 'transparent' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 3px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .initials {
    font-size: 2rem;
    font-weight: 600;
    color: white;
    text-transform: uppercase;
  }
`;

const UserInfo = styled.div`
  text-align: center;
  width: 100%;
`;

const UserName = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: white;
  margin-bottom: 0.25rem;
`;

const UserRole = styled.div`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: capitalize;
`;

const UserBadge = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 0.25rem;
`;

const NavSection = styled.div`
  flex: 1;
  padding: 1rem 0;
`;

const NavItem = styled.button`
  width: 100%;
  padding: ${props => props.isSubItem ? '0.75rem 1.5rem 0.75rem 3rem' : '0.875rem 1.5rem'};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: ${props => props.active ? 'rgba(102, 126, 234, 0.15)' : 'transparent'};
  border: none;
  color: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.85)'};
  font-size: ${props => props.isSubItem ? '0.875rem' : '0.9375rem'};
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  border-left: 4px solid ${props => props.active ? '#667eea' : 'transparent'};
  position: relative;
  
  &:hover {
    background: ${props => props.active ? 'rgba(102, 126, 234, 0.2)' : 'rgba(255, 255, 255, 0.08)'};
    color: white;
  }
  
  svg {
    width: ${props => props.isSubItem ? '18px' : '20px'};
    height: ${props => props.isSubItem ? '18px' : '20px'};
    flex-shrink: 0;
    opacity: ${props => props.active ? '1' : '0.9'};
    transition: opacity 0.2s ease;
  }
`;

const NavGroup = styled.div`
  margin-bottom: 0.25rem;
`;

const NavGroupHeader = styled.button`
  width: 100%;
  padding: 0.875rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  position: relative;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: white;
  }
  
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
  
  .chevron {
    margin-left: auto;
    width: 16px;
    height: 16px;
    transition: transform 0.2s ease;
    transform: ${props => props.isOpen ? 'rotate(0deg)' : 'rotate(-90deg)'};
  }
`;

const NavGroupItems = styled.div`
  display: ${props => props.isOpen ? 'block' : 'none'};
  background: rgba(0, 0, 0, 0.15);
`;

const NotificationSection = styled.div`
  padding: 1rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
`;

const NotificationButton = styled.button`
  width: 100%;
  padding: 0.875rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  position: relative;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
  
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const NotificationBadge = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 1rem;
  background: #ef4444;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const NotificationDropdown = styled.div`
  /* Fixed popover beside the sidebar so it never covers the profile/nav. */
  position: fixed;
  left: 272px;
  bottom: 18px;
  width: min(360px, calc(100vw - 292px));
  background: white;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  max-height: 70vh;
  overflow-y: auto;
  z-index: 3000;
  display: ${props => props.isOpen ? 'block' : 'none'};

  @media (max-width: 768px) {
    left: 12px;
    right: 12px;
    bottom: 76px;
    width: auto;
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    
    &:hover {
      background: #94a3b8;
    }
  }
`;

const NotificationHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 8px 8px 0 0;
`;

const NotificationHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NotificationTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
`;

const NotificationFilterRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e2e8f0;
  font-size: 0.75rem;
  line-height: 1.35;
  color: #64748b;
  cursor: pointer;
  user-select: none;

  input {
    margin-top: 0.1rem;
    flex-shrink: 0;
    accent-color: ${p => p.theme.colors.accent};
    cursor: pointer;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
  
  &:hover {
    background: #e2e8f0;
    color: #1e293b;
  }
`;

const NotificationList = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const NotificationItem = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationDismissBtn = styled.button`
  background: transparent;
  border: none;
  padding: 0.25rem;
  margin: -0.25rem -0.25rem -0.25rem 0;
  cursor: pointer;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: #ef4444;
    background: rgba(239, 68, 68, 0.08);
  }
`;

const NotificationItemTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
`;

const NotificationItemMessage = styled.div`
  font-size: 0.8125rem;
  color: #64748b;
  line-height: 1.4;
`;

const NotificationItemTime = styled.div`
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 0.5rem;
`;

const EmptyNotifications = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  color: #94a3b8;
  font-size: 0.875rem;
`;

const PeerRatingBanner = styled.div`
  margin: 0.75rem;
  padding: 0.85rem 1rem;
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.12) 100%);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 10px;
  animation: pulseGlow 3s ease-in-out infinite;

  @keyframes pulseGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.0); }
    50% { box-shadow: 0 0 8px 2px rgba(251, 191, 36, 0.15); }
  }
`;

const PeerRatingHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  color: #fbbf24;
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const PeerRatingNote = styled.div`
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.5;
  white-space: pre-wrap;
  max-height: 120px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(251, 191, 36, 0.3);
    border-radius: 2px;
  }
`;

const PeerRatingTime = styled.div`
  font-size: 0.6875rem;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 0.4rem;
`;

const LogoutButton = styled(NavItem)`
  margin-top: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 1rem;
  color: rgba(255, 255, 255, 0.8);
  
  &:hover {
    background: rgba(239, 68, 68, 0.1);
    color: #fca5a5;
  }
`;

const Sidebar = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationsUnreadOnly, setNotificationsUnreadOnly] = useState(() => {
    try {
      const v = localStorage.getItem('sales_sidebar_notifications_unread_only');
      if (v === null) return true;
      return v === 'true';
    } catch {
      return true;
    }
  });
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [storeInfo, setStoreInfo] = useState(null);
  const [peerRating, setPeerRating] = useState(null);
  const [peerRatingActive, setPeerRatingActive] = useState(false);
  const [isOrdersGroupOpen, setIsOrdersGroupOpen] = useState(true);
  const notificationRef = useRef(null);
  const dropdownRef = useRef(null);

  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return first + last || 'U';
  };

  const fullName = user?.fullName ||
    `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
    user?.name ||
    'User';

  // Fetch store information if not already in user object
  useEffect(() => {
    const fetchStoreInfo = async () => {
      // First check if store info is in user object
      if (user?.store) {
        setStoreInfo(user.store);
      }

      // Check localStorage for store info
      if (!user?.store) {
        const storedStoreInfo = localStorage.getItem('storeInfo');
        if (storedStoreInfo) {
          try {
            setStoreInfo(JSON.parse(storedStoreInfo));
          } catch (e) {
            console.error('Error parsing stored store info:', e);
          }
        }
      }

      // Resolve store ID from several possible sources
      let storeId = user?.store?.id || user?.store?._id || user?.storeId;
      if (!storeId) {
        try {
          const ls = JSON.parse(localStorage.getItem('storeInfo') || '{}');
          storeId = ls.id || ls._id;
        } catch (_) { /* ignore */ }
      }
      if (!storeId) {
        try {
          const lsUser = JSON.parse(localStorage.getItem('user') || '{}');
          storeId = lsUser?.store?.id || lsUser?.store?._id || lsUser?.storeId;
        } catch (_) { /* ignore */ }
      }

      if (storeId) {
        try {
          const token = localStorage.getItem('token');
          if (!token) return;
          const response = await axios.get(`/api/stores/${storeId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data?.store) {
            const s = response.data.store;
            setStoreInfo({ name: s.name, code: s.storeCode, logo: s.logo || null });
            localStorage.setItem('storeInfo', JSON.stringify({ name: s.name, code: s.storeCode, logo: s.logo || null }));
            if (s.peerRating) setPeerRating(s.peerRating);
          }
        } catch (error) {
          console.error('Error fetching store info:', error);
        }
      }

    };

    fetchStoreInfo();
  }, [user]);

  // Check if peer rating window is currently active
  useEffect(() => {
    if (!peerRating) { setPeerRatingActive(false); return; }

    const check = () => {
      const now = new Date();
      const dayOfWeek = now.getDay();
      const hour = now.getHours();
      const wd = typeof peerRating.visibleWeekday === 'number' ? peerRating.visibleWeekday : 1;
      const hStart = typeof peerRating.visibleHourStart === 'number' ? peerRating.visibleHourStart : 8;
      const hEnd = typeof peerRating.visibleHourEnd === 'number' ? peerRating.visibleHourEnd : 18;

      const isActive = dayOfWeek === wd && hour >= hStart && hour < hEnd;
      setPeerRatingActive(isActive);
    };

    check();
    const interval = setInterval(check, 60000);
    return () => clearInterval(interval);
  }, [peerRating]);

  // Check if we're on the main visitor entry page
  // "New Visit" = returning-client lookup; "New order" = the blank walk-in flow.
  const isReturningLookup = location.pathname === '/visitor-entry/returning';
  const isBlankEntry = location.pathname === '/visitor-entry' ||
    location.pathname === '/visitor-entry/';
  const isMainEntry = isReturningLookup || isBlankEntry;

  // Check if any orders sub-item is active to keep the group expanded
  const isAnyOrdersActive =
    location.pathname === '/visitor-entry/standby' ||
    location.pathname === '/visitor-entry/track' ||
    location.pathname === '/visitor-entry/search';

  // Auto-expand orders group if any sub-item is active
  useEffect(() => {
    if (isAnyOrdersActive) {
      setIsOrdersGroupOpen(true);
    }
  }, [isAnyOrdersActive]);

  const topMenuItems = [
    {
      id: 'overview',
      label: t('nav.overview'),
      icon: LayoutDashboard,
      path: '/sales',
      active: location.pathname === '/sales' || location.pathname === '/sales/'
    },
    {
      id: 'new-visit',
      label: t('nav.newVisit'),
      icon: User,
      path: '/visitor-entry/returning',
      active: isReturningLookup
    },
    {
      id: 'new-order',
      label: t('nav.newOrder', 'New order'),
      icon: ShoppingBag,
      path: '/visitor-entry',
      active: isBlankEntry
    }
  ];

  const ordersMenuItems = [
    {
      id: 'standby',
      label: t('nav.standby', 'Standby'),
      icon: PauseCircle,
      path: '/visitor-entry/standby',
      active: location.pathname === '/visitor-entry/standby'
    },
    {
      id: 'track',
      label: t('nav.trackOrder'),
      icon: PackageSearch,
      path: '/visitor-entry/track',
      active: location.pathname === '/visitor-entry/track'
    },
    {
      id: 'search',
      label: t('nav.unsuccessfulOrders'),
      icon: Search,
      path: '/visitor-entry/search',
      active: location.pathname === '/visitor-entry/search'
    }
  ];

  // Store managers use the sales flow too — give them a way back to their dashboard
  const storedRole = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').role; } catch { return null; }
  })();
  const isManager = (user?.role || storedRole) === 'store_manager';

  const bottomMenuItems = [
    ...(isManager ? [{
      id: 'manager-dashboard',
      label: t('nav.managerDashboard'),
      icon: LayoutDashboard,
      path: '/manager/dashboard',
      active: location.pathname.startsWith('/manager/dashboard')
    }] : []),
    {
      id: 'clients',
      label: t('nav.clients'),
      icon: Users,
      path: '/clients',
      active: location.pathname === '/clients'
    },
    {
      id: 'doctors',
      label: t('nav.doctors'),
      icon: Stethoscope,
      path: '/doctors',
      active: location.pathname === '/doctors'
    },
    {
      id: 'occupations',
      label: t('nav.occupations'),
      icon: Briefcase,
      path: '/occupations',
      active: location.pathname === '/occupations'
    },
    ...(peerRatingActive ? [{
      id: 'peer-rating',
      label: t('nav.peerRating'),
      icon: ClipboardList,
      path: '/peer-rating',
      active: location.pathname === '/peer-rating'
    }] : [])
  ];

  // Fetch notifications: real ones from /api/notifications + the
  // legacy "incomplete orders" alerts for sales-side users. Both feeds
  // are merged into a single list keyed by `id`, sorted by time. The
  // unread badge counts both kinds. Refreshed every 30 seconds.
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user?.id) return;
      const token = localStorage.getItem('token');
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };

      // Run both requests in parallel; never let one failure hide the
      // other. We tolerate partial responses.
      const notifQuery = notificationsUnreadOnly ? 'limit=20&unread=true' : 'limit=20';
      const [notifResp, incompleteResp] = await Promise.allSettled([
        axios.get(`/api/notifications?${notifQuery}`, { headers }),
        axios.get(`/api/orders/incomplete/${user.id}`, { headers }),
      ]);

      const merged = [];
      let unread = 0;

      if (notifResp.status === 'fulfilled' && notifResp.value?.data?.items) {
        // These are central-store / lab concerns (lens sourcing, supplier orders)
        // and shouldn't clutter the sales sidebar — hide them here.
        const HIDE_FROM_SALES = new Set(['lens_out_of_stock', 'lens_pulled_from_stock']);
        const items = (notifResp.value.data.items || []).filter((n) => !HIDE_FROM_SALES.has(n.type));
        items.forEach((n) => {
          if (!n.read) unread += 1;
          merged.push({
            id: `notif-${n._id}`,
            kind: 'notification',
            sourceId: n._id,
            type: n.type || 'info',
            title: n.title,
            message: n.body || '',
            time: n.createdAt,
            link: n.link || null,
            read: !!n.read,
            data: n,
          });
        });
      }

      if (incompleteResp.status === 'fulfilled' && incompleteResp.value?.data?.incompleteOrders) {
        const incompleteOrders = incompleteResp.value.data.incompleteOrders;
        unread += incompleteOrders.length;
        incompleteOrders.slice(0, 5).forEach((order, index) => {
          merged.push({
            id: `incomplete-${order.prescription?._id || index}`,
            kind: 'incomplete_order',
            type: 'incomplete_order',
            title: 'Unsuccessful Order',
            message: order.prescription?.patientName
              ? `Order for ${order.prescription.patientName} is unsuccessful`
              : 'You have an unsuccessful order',
            time: order.prescription?.createdAt || new Date(),
            read: false,
            data: order,
          });
        });
      }

      merged.sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));

      setNotifications(merged);
      setNotificationCount(unread);
    };

    fetchNotifications().catch((err) => console.error('Error fetching notifications:', err));

    // Refresh notifications every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications().catch((err) => console.error('Error refreshing notifications:', err));
    }, 30000);

    return () => clearInterval(interval);
  }, [user?.id, notificationsUnreadOnly]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setIsNotificationOpen(false);
      }
    };

    if (isNotificationOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificationOpen]);

  const handleNavClick = (path) => {
    // "New Visit" starts a brand-new sales flow: clear any in-progress
    // visit/order session so the lookup/form opens empty. (Returning to an
    // in-progress visit is done via the progress bar, not this button.)
    if (path === '/visitor-entry' || path === '/visitor-entry/returning') {
      clearSalesFlowSession();
    }
    navigate(path);
  };

  const handleNotificationClick = () => {
    setIsNotificationOpen(!isNotificationOpen);
  };

  const handleNotificationItemClick = async (notification) => {
    // Real notifications: PATCH read on click, then navigate to link.
    if (notification.kind === 'notification' && notification.sourceId) {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          await axios.patch(
            `/api/notifications/${notification.sourceId}/read`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      } catch (err) {
        // Non-blocking: even if mark-read fails we still navigate.
        console.warn('Mark notification read failed:', err.message);
      }
      // Optimistically reflect in local state so the badge updates
      // before the next 30s poll.
      setNotifications((prev) => prev.map((n) =>
        n.id === notification.id ? { ...n, read: true } : n
      ));
      setNotificationCount((c) => Math.max(0, c - 1));

      // Sanitize the destination. We used to emit `/order-tracking/<id>`
      // for everyone, which doesn't exist as a route — the catch-all
      // route then redirected to "/" (the login page) and looked like
      // a logout. Rewrite any such link to the sales overview with the
      // order highlighted.
      let dest = notification.link;
      if (dest && /^\/order-tracking\//.test(dest)) {
        const ref = dest.replace(/^\/order-tracking\//, '');
        dest = `/sales?highlight=${encodeURIComponent(ref)}`;
      }
      if (dest) navigate(dest);
      setIsNotificationOpen(false);
      return;
    }

    // Legacy "incomplete order" entries: same behavior as before.
    if (notification.type === 'incomplete_order') {
      navigate('/visitor-entry/search');
    }
    setIsNotificationOpen(false);
  };

  const handleDismissNotification = async (e, notification) => {
    e.stopPropagation(); // Prevent triggering the row click / navigation

    // Real notifications: PATCH read on server
    if (notification.kind === 'notification' && notification.sourceId) {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          await axios.patch(
            `/api/notifications/${notification.sourceId}/read`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      } catch (err) {
        console.warn('Dismiss notification failed:', err.message);
      }
    }

    // Optimistically remove from local state
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    setNotificationCount((c) => Math.max(0, c - 1));
  };

  const formatTime = (date) => {
    if (!date) return '';
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInSeconds = Math.floor((now - notificationDate) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  return (
    <SidebarContainer>
      <UserCard>
        <Avatar hasImage={!!user?.profileImage}>
          {user?.profileImage ? (
            <img src={user.profileImage} alt={fullName} />
          ) : (
            <div className="initials">{getInitials(user?.firstName, user?.lastName)}</div>
          )}
        </Avatar>
        <UserInfo>
          <UserName>{fullName}</UserName>
          <UserRole>Sales</UserRole>
          {storeInfo?.name && (
            <UserBadge>{storeInfo.name}</UserBadge>
          )}
        </UserInfo>
      </UserCard>

      <NavSection>
        {topMenuItems.map(item => {
          const Icon = item.icon;
          return (
            <NavItem
              key={item.id}
              active={item.active}
              onClick={() => handleNavClick(item.path)}
            >
              <Icon />
              {item.label}
            </NavItem>
          );
        })}

        <NavGroup>
          <NavGroupHeader
            isOpen={isOrdersGroupOpen}
            onClick={() => setIsOrdersGroupOpen(!isOrdersGroupOpen)}
          >
            <ShoppingBag />
            {t('nav.orders')}
            <ChevronDown className="chevron" />
          </NavGroupHeader>
          <NavGroupItems isOpen={isOrdersGroupOpen}>
            {ordersMenuItems.map(item => {
              const Icon = item.icon;
              return (
                <NavItem
                  key={item.id}
                  active={item.active}
                  isSubItem={true}
                  onClick={() => handleNavClick(item.path)}
                >
                  <Icon />
                  {item.label}
                </NavItem>
              );
            })}
          </NavGroupItems>
        </NavGroup>

        {bottomMenuItems.map(item => {
          const Icon = item.icon;
          return (
            <NavItem
              key={item.id}
              active={item.active}
              onClick={() => handleNavClick(item.path)}
            >
              <Icon />
              {item.label}
            </NavItem>
          );
        })}
      </NavSection>

      {peerRatingActive && peerRating && (
        <PeerRatingBanner onClick={() => navigate('/peer-rating')} style={{ cursor: 'pointer' }}>
          <PeerRatingHeader>
            <ClipboardList />
            Peer Rating Active
          </PeerRatingHeader>
          {peerRating.setupNote ? (
            <PeerRatingNote>{peerRating.setupNote}</PeerRatingNote>
          ) : (
            <PeerRatingNote style={{ fontStyle: 'italic', opacity: 0.7 }}>
              Tap here to rate your peers today.
            </PeerRatingNote>
          )}
          <PeerRatingTime>
            {(() => {
              const h = (v) => { const d = new Date(); d.setHours(v, 0); return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); };
              return `Until ${h(peerRating.visibleHourEnd)}`;
            })()}
          </PeerRatingTime>
        </PeerRatingBanner>
      )}

      <NotificationSection>
        <NotificationButton
          ref={notificationRef}
          onClick={handleNotificationClick}
        >
          <Bell />
          {t('nav.notifications')}
          {notificationCount > 0 && (
            <NotificationBadge>
              {notificationCount > 9 ? '9+' : notificationCount}
            </NotificationBadge>
          )}
        </NotificationButton>

        <NotificationDropdown ref={dropdownRef} isOpen={isNotificationOpen}>
          <NotificationHeader>
            <NotificationHeaderRow>
              <NotificationTitle>{t('nav.notifications')}</NotificationTitle>
              <CloseButton onClick={() => setIsNotificationOpen(false)}>
                <X size={18} />
              </CloseButton>
            </NotificationHeaderRow>
            <NotificationFilterRow>
              <input
                type="checkbox"
                checked={notificationsUnreadOnly}
                onChange={(e) => {
                  const next = e.target.checked;
                  setNotificationsUnreadOnly(next);
                  try {
                    localStorage.setItem('sales_sidebar_notifications_unread_only', String(next));
                  } catch { /* ignore */ }
                }}
              />
              <span>
                Only unread — notifications you have opened or dismissed stay hidden. Uncheck to see recent history.
              </span>
            </NotificationFilterRow>
          </NotificationHeader>

          <NotificationList>
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  onClick={() => handleNotificationItemClick(notification)}
                >
                  <NotificationContent>
                    <NotificationItemTitle>{notification.title}</NotificationItemTitle>
                    <NotificationItemMessage>{notification.message}</NotificationItemMessage>
                    <NotificationItemTime>{formatTime(notification.time)}</NotificationItemTime>
                  </NotificationContent>
                  <NotificationDismissBtn
                    onClick={(e) => handleDismissNotification(e, notification)}
                    title="Dismiss"
                  >
                    <X size={14} />
                  </NotificationDismissBtn>
                </NotificationItem>
              ))
            ) : (
              <EmptyNotifications>
                No new notifications
              </EmptyNotifications>
            )}
          </NotificationList>
        </NotificationDropdown>
      </NotificationSection>

      <LogoutButton onClick={onLogout}>
        <LogOut />
        {t('nav.signOut')}
      </LogoutButton>
    </SidebarContainer>
  );
};

export default Sidebar;

